/// <reference types="@emotion/react/types/css-prop" />
import {
  ContentModule,
  SchemaInstructionsHelper,
  useShowInstructions,
} from '@backstage-components/base';
import {useSubscription} from 'observable-hooks';
import {useEffect, useReducer, useRef, type FC, type Reducer} from 'react';
import {ComponentDefinition, SchemaType} from './AudioDefinition';

export type AudioComponentDefinition = ContentModule<'Audio', SchemaType>;

export const AudioComponent: FC<AudioComponentDefinition> = (definition) => {
  const {props} = definition;
  const {
    shouldAutoplay,
    shouldLoop,
    source,
    audioVolume,
    preload,
    playFromBeginning,
  } = props;
  const $ref = useRef<HTMLAudioElement | null>(null);
  // Set the initial unmuted volume to the audioVolume property set in the module settings. The
  // value is set when the `$ref` is populated
  const unmuteVolume = useRef(audioVolume);
  const [status, dispatch] = useReducer(reducer, 'initial');
  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );
  // Handle setting audio volume
  const setVolume = (volume: number): void => {
    const el = $ref.current;
    if (el === null) {
      return;
    } else if (volume >= 0 && volume <= 1) {
      el.volume = volume;
    } else {
      throw new Error(`Volume must be in the range [0,1]. Got ${volume}`);
    }
  };
  // Handle muting the audio
  const mute = (): void => setVolume(0);
  // Handle pausing the audio
  const pause = (): void => {
    const el = $ref.current;
    if (el === null) {
      return;
    } else if (status === 'playing') {
      el.pause();
    }
  };
  // Handle playing the audio
  const play = (): void => {
    const el = $ref.current;
    if (el === null) {
      return;
    } else if (status === 'ready' || status === 'playing') {
      // Play the audio from the beginning
      if (playFromBeginning) el.currentTime = 0;
      el.play();
    } else {
      console.debug(
        `${definition.id} not in 'ready' status. Current status: ${status}`
      );
    }
  };
  // Handle resetting to previous volume level
  const unmute = (): void => setVolume(unmuteVolume.current);
  // Listen for commands from flows
  useSubscription(observable, (instruction): void => {
    dispatch(instruction);
    switch (instruction.type) {
      case 'Audio:mute':
        return mute();
      case 'Audio:pause':
        return pause();
      case 'Audio:play':
        return play();
      case 'Audio:unmute':
        return unmute();
    }
  });
  // If audio loading has not started when mount finishes, load source
  useEffect(() => {
    if (status === 'initial') {
      $ref.current?.load();
    }
  }, [status]);

  useEffect(() => {
    setVolume(audioVolume);
  }, [audioVolume]);

  return (
    <audio
      crossOrigin="anonymous"
      preload={shouldAutoplay || preload ? 'auto' : 'none'}
      src={source.url}
      loop={shouldLoop}
      autoPlay={shouldAutoplay}
      onCanPlay={() => dispatch({type: 'Audio:on-can-play'})}
      onCanPlayThrough={() => dispatch({type: 'Audio:on-can-play-through'})}
      onEnded={() => broadcast({type: 'Audio:on-complete', meta: {}})}
      onLoadStart={() => dispatch({type: 'Audio:on-load-start'})}
      onPause={() => broadcast({type: 'Audio:on-pause', meta: {}})}
      onPlay={() => broadcast({type: 'Audio:on-start', meta: {}})}
      onVolumeChange={(e) => {
        const currentVolume = e.currentTarget.volume;
        if (currentVolume === 0) {
          broadcast({type: 'Audio:on-mute', meta: {}});
        } else if (unmuteVolume.current === 0) {
          broadcast({type: 'Audio:on-unmute', meta: {}});
        }
        // Only keep the previous, unmuted, volume
        if (currentVolume !== 0) {
          unmuteVolume.current = currentVolume;
        }
      }}
      ref={(el) => {
        // The ref callback function is being called when media plays, sometimes
        // with a `null` value, after the initial mount. Without the guard, when
        // the ref callback is invoked with a `null` value the `unmuteVolume`
        // would be set to `0` causing mute/unmute behavior to be incorrect.
        if ((el === null || el === $ref.current) && $ref.current !== null) {
          return;
        }
        $ref.current = el;
        unmuteVolume.current = el?.volume ?? 0;
      }}
      data-name={definition.name}
      data-testid="audio-module"
    />
  );
};

type Action =
  | SchemaInstructionsHelper<typeof ComponentDefinition>
  | {type: 'Audio:on-load-start'}
  | {type: 'Audio:on-can-play'}
  | {type: 'Audio:on-can-play-through'};

type Status = 'initial' | 'loading' | 'ready' | 'playing';

/**
 * Manage the status of the audio element to track playing and playability state
 */
const reducer: Reducer<Status, Action> = (draft, action) => {
  if (draft === 'initial' && action.type === 'Audio:on-load-start') {
    return 'loading';
  } else if (
    (draft === 'initial' || draft === 'loading') &&
    (action.type === 'Audio:on-can-play' ||
      action.type === 'Audio:on-can-play-through')
  ) {
    return 'ready';
  } else if (draft === 'ready' && action.type === 'Audio:on-start') {
    return 'playing';
  } else if (
    draft === 'playing' &&
    (action.type === 'Audio:on-pause' || action.type === 'Audio:on-complete')
  ) {
    return 'ready';
  } else {
    return draft;
  }
};
